import { fetchApiJson, trackError, trackUserEvent } from '@vend/utilities'
import { ApiCustomer } from '@vend/types'
import { LineItem } from '../../models/line-item'
import { SaleDiscount } from '../../models/sale-discount'

export type InvoicingQuoteLineItem = {
  productName: string
  sku: string
  quantity: number
  unitPrice: string
  taxRate: number
  discountPercent: number
  note: string
  metadata: Record<string, string>
}

export type InvoicingQuotePayload = {
  source: string
  pli: string
  lineItems: InvoicingQuoteLineItem[]
  customer: {
    name: string
    email: string
  }
  note: string
  saleDiscount: {
    amount: string
    isPercentage: boolean
  } | null
  metadata: Record<string, string>
}

export type CreateInvoicingQuoteResponse = {
  success: boolean
  quoteId?: string
  error?: string
}

/**
 * Maps sale line items to the invoicing quote payload format.
 */
export const mapLineItems = (
  lineItems: LineItem[]
): InvoicingQuoteLineItem[] => {
  return lineItems.map(lineItem => ({
    productName: lineItem.product?.name || '',
    sku: lineItem.product?.sku || '',
    quantity: Number(lineItem.quantity),
    unitPrice: String(lineItem.price),
    taxRate: lineItem.taxInfo?.rates?.[0]?.rate || 0,
    discountPercent: Number(lineItem.displayDiscountPercent) || 0,
    note: lineItem.note || '',
    metadata: {
      productId: lineItem.product?.id || '',
      lineItemLocalId: lineItem.localId,
    },
  }))
}

/**
 * Builds the full payload for the invoicing quote API.
 */
export const buildQuotePayload = ({
  retailerId,
  outletId,
  registerId,
  userId,
  lineItems,
  customer,
  note,
  discount,
  displayDiscount,
  discountIsPercentage,
}: {
  retailerId: string | undefined
  outletId: string | undefined
  registerId: string | undefined
  userId: string | undefined
  lineItems: LineItem[]
  customer: ApiCustomer | null | undefined
  note: string | undefined
  discount: SaleDiscount | null | undefined
  displayDiscount: number | string
  discountIsPercentage: boolean
}): InvoicingQuotePayload => {
  const hasSaleDiscount = discount && parseFloat(String(discount.price)) !== 0

  return {
    source: 'x_series_sale',
    pli: `lsx:${outletId}` || '',
    lineItems: mapLineItems(lineItems),
    customer: {
      name: customer
        ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
        : '',
      email: customer?.email || '',
    },
    note: note || '',
    saleDiscount: hasSaleDiscount
      ? {
          amount: String(displayDiscount),
          isPercentage: discountIsPercentage,
        }
      : null,
    metadata: {
      registerId: registerId || '',
      userId: userId || '',
    },
  }
}

/**
 * Creates a quote via the invoicing quote service proxy.
 *
 * @param payload The quote payload to send
 * @param idempotencyKey A unique key to prevent duplicate quote creation
 * @returns The created quote ID on success, or an error
 */
export const createInvoicingQuote = async (
  payload: InvoicingQuotePayload,
  idempotencyKey: string,
  tenantRef: string
): Promise<CreateInvoicingQuoteResponse> => {
  try {
    const response = await fetchApiJson(
      '/api/2.0/invoicing-quotes/v1/quotes/populated',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': idempotencyKey,
          'X-Tenant-Ref': tenantRef,
        },
        body: JSON.stringify(payload),
      }
    )

    if (response.error || !response.id) {
      const errorMessage =
        response.message || response.error || 'Failed to create quote.'
      trackError('Invoicing Quote Creation Error', errorMessage, {
        source: payload.source,
        tenantRef,
      })
      return {
        success: false,
        error: errorMessage,
      }
    }

    trackUserEvent('Quotes', 'Invoicing Quote Created', {
      quoteId: response.id,
      source: payload.source,
      lineItemCount: payload.lineItems.length,
    })

    return {
      success: true,
      quoteId: response.id,
    }
  } catch (err: any) {
    const errorMessage = err?.message || 'Unknown error occurred'
    trackError('Invoicing Quote Creation Error', errorMessage, {
      source: payload.source,
      tenantRef,
    })

    return {
      success: false,
      error: errorMessage,
    }
  }
}
